'use client';

import { GetResponseType } from '@/api/types/api';
import { ApiResponseDefinition } from '@/api/types/api-definition';
import CountrySiteList from '@/components/CountrySiteList';
import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import { setSession } from '@/utils/tools';
import { useCustomerService } from '@/hooks/useCustomerService';
import {
  FacebookOutlined,
  HelpEmailOutlined,
  HelpOnlineOutlined,
  HelpPhoneOutlined,
  InstagramOutlined,
  PinterestOutlined,
  TiktokOutlined,
  TwitterOutlined,
  YoutubeOutlined,
} from '@zeelool/icon';
import { useLocale } from 'next-intl';
import { useTranslations } from '@/i18n/use-translations';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import SubscribeCoupon from './SubscribeCoupon';
import Subscribe_US from './Subscribe_US';
import { IS_US, IS_FR, IS_DE } from '@/utils/constants';
import { shouldShowUSFooterSubscribe } from './subscribeVisibility';

const Footer = ({
  navList,
  SDSocialMediaClick,
  SDBottomNavTabClick,
  isShowSubscribe,
  deviceType,
}: {
  navList: GetResponseType<ApiResponseDefinition['GET:/api/nav/lists']>;
  SDSocialMediaClick: (btn: string) => void;
  SDBottomNavTabClick: (
    first_tab_name: string,
    second_tab_name?: string,
    first_tab_index?: number,
    second_tab_index?: number,
  ) => void;
  isShowSubscribe: boolean;
  deviceType: EPlatform;
}) => {
  const bottomSubscribeBoxTxt = (navList[0]?.content as { describe: string })?.describe || '';
  const pathname = usePathname();
  const t = useTranslations('layout.footer');
  const locale = useLocale();
  const router = useRouter();
  const isShowCouponSubscribe = !IS_US && isShowSubscribe;
  const isShowSubscribeUS = shouldShowUSFooterSubscribe(pathname);
  const paymentMethodsPicture = IS_FR
    ? '/zeelool-next/home-page/footer_accept_fr.png'
    : '/zeelool-next/home-page/footer_accept.png';

  // 使用客服服务 Hook（只提供打开客服的函数）
  const { openCustomerService } = useCustomerService({
    inAppH5: false, // APP 此组件永不展示
  });
  // 处理在线聊天
  const handleLiveChat = () => {
    openCustomerService();
  };
  return (
    <div>
      {isShowSubscribeUS && <Subscribe_US />}
      {isShowCouponSubscribe && <SubscribeCoupon bottomSubscribeBoxTxt={bottomSubscribeBoxTxt} />}

      <div className={`bg-black ${pathname.includes('/goods-detail/') ? 'pb-[80px]' : ''}`}>
        <div className="flex justify-between flex-wrap w-full p-[102px_48px_24px] box-border">
          <dl className="text-primary-white mr-[10%]">
            <dt className="mb-16 font-bb text-[56px]">{t('needHelp')}</dt>
            <dd className="flex items-center justify-start mb-20">
              <HelpOnlineOutlined className="text-20 mr-18" />
              <div className="height-fit">
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <p
                    className="text-primary-white text-14 block cursor-pointer"
                    role="button"
                    aria-label={t('liveChat')}
                    onClick={handleLiveChat}
                  >
                    {t('liveChat')}
                  </p>
                ) : (
                  <Link
                    prefetch={false}
                    className="text-primary-white text-14 block"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick('Need Help?', 'Contact Us', 0, 0);
                      router.push('/contact-zeelool');
                    }}
                  >
                    {t('contactUs')}
                  </Link>
                )}
              </div>
            </dd>
            <dd className="flex items-center justify-start mb-20">
              <HelpEmailOutlined className="text-20 mr-18" />
              <div>
                {env.NEXT_PUBLIC_FULL_SITE_NAME === 'ZEELOOL FR' ? (
                  <Link
                    prefetch={false}
                    className="text-primary-white text-14 block"
                    aria-label={t('contactUs')}
                    href="/contact-zeelool"
                    onClick={(event) => {
                      event.preventDefault();
                      SDBottomNavTabClick('Need Help?', 'Contact Us', 0, 0);
                      router.push('/contact-zeelool');
                    }}
                  >
                    {t('ContactUsViaForm')}
                  </Link>
                ) : (
                  <p className="text-primary-white text-14 block mb-[6px]">
                    {t('businessCooperation')}
                  </p>
                )}
                <Link
                  prefetch={false}
                  className="text-primary-white text-14 block"
                  href={`mailto:${env.NEXT_PUBLIC_MARKET_EMAIL}`}
                >
                  {env.NEXT_PUBLIC_MARKET_EMAIL}
                </Link>
              </div>
            </dd>
            {env.NEXT_PUBLIC_FOOTER_SHOW_TEL === 'true' && (
              <dd className="flex items-center justify-start mb-20">
                <HelpPhoneOutlined className="text-20 mr-18" />
                <div>
                  <p className="text-primary-white text-14 block mb-[6px]">{t('callInfo')}</p>
                  <p className="text-primary-white text-14 block">
                    {env.NEXT_PUBLIC_CUSTOMER_SERVICE_TIME}
                  </p>
                </div>
              </dd>
            )}
            <dd className="flex items-center justify-start mb-20">
              <Link
                prefetch={false}
                href="/track-order"
                aria-label={t('trackOrder')}
                className="text-primary-white bg-primary-purple text-16 block w-[210px] h-[48px] mt-[40px] text-center leading-[48px] fpnt-bb rounded-[24px]"
                onClick={(event) => {
                  event.preventDefault();
                  SDBottomNavTabClick('Need Help?', 'Track Order', 0, 0);
                  router.push('/track-order');
                }}
              >
                {t('trackOrder')}
              </Link>
            </dd>
            <dd className="flex items-center justify-start mb-20 text-primary-white mt-[40px]">
              <span className="text-16 font-bb">{t('followUs')}</span>
              <div className="flex items-center justify-between ml-[30px]">
                {env.NEXT_PUBLIC_FB && (
                  <Link
                    prefetch={false}
                    className="flex-1 mr-[20px] "
                    href={env.NEXT_PUBLIC_FB}
                    aria-label={t('social.joinGroup')}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    onClick={() => SDSocialMediaClick('fb')}
                  >
                    <FacebookOutlined className="text-[30px] text-primary-white" />
                  </Link>
                )}
                {!!env.NEXT_PUBLIC_YTB && (
                  <Link
                    prefetch={false}
                    className="flex-1 mr-[20px] "
                    href={env.NEXT_PUBLIC_YTB}
                    aria-label={t('social.joinGroup')}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    onClick={() => SDSocialMediaClick('youtube')}
                  >
                    <YoutubeOutlined className="text-[30px] text-primary-white" />
                  </Link>
                )}
                {!!env.NEXT_PUBLIC_INS && (
                  <Link
                    prefetch={false}
                    href={env.NEXT_PUBLIC_INS}
                    className="flex-1 mr-[20px] "
                    target="_blank"
                    aria-label={t('social.joinGroup')}
                    rel="noopener nofollow noreferrer"
                    onClick={() => SDSocialMediaClick('ins')}
                  >
                    <InstagramOutlined className="text-[30px] text-primary-white" />
                  </Link>
                )}
                {!!env.NEXT_PUBLIC_X && (
                  <Link
                    prefetch={false}
                    href={env.NEXT_PUBLIC_X}
                    target="_blank"
                    aria-label={t('social.joinGroup')}
                    rel="noopener nofollow noreferrer"
                    className="flex-1 mr-[20px] "
                    onClick={() => SDSocialMediaClick('twitter')}
                  >
                    <TwitterOutlined className="text-[30px] text-primary-white" />
                  </Link>
                )}
                {!!env.NEXT_PUBLIC_PIN && (
                  <Link
                    prefetch={false}
                    href={env.NEXT_PUBLIC_PIN}
                    target="_blank"
                    aria-label={t('social.joinGroup')}
                    rel="noopener nofollow noreferrer"
                    className="flex-1 mr-[20px] "
                    onClick={() => SDSocialMediaClick('pinterest')}
                  >
                    <PinterestOutlined className="text-[30px] text-primary-white" />
                  </Link>
                )}
                {!!env.NEXT_PUBLIC_TT && (
                  <Link
                    prefetch={false}
                    href={env.NEXT_PUBLIC_TT}
                    aria-label={t('social.joinGroup')}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    className="flex-1"
                    onClick={() => SDSocialMediaClick('tiktok')}
                  >
                    <TiktokOutlined className="text-[30px] text-primary-white" />
                  </Link>
                )}
                {env.NEXT_PUBLIC_WEB_SHOW_FB_GROUP === 'true' && (
                  <Link
                    prefetch={false}
                    href={env.NEXT_PUBLIC_FB_GROUP}
                    aria-label={t('social.joinGroup')}
                    target="_blank"
                    rel="noopener nofollow noreferrer"
                    className="ml-[30px]"
                  >
                    <Image
                      src={env.NEXT_PUBLIC_FB_GROUP_IMG}
                      alt={t('social.joinGroup')}
                      width={204}
                      height={48}
                    />
                  </Link>
                )}
              </div>
            </dd>
            <dd className="mt-[34px]">
              <CountrySiteList
                onCountryClick={(shortName) => SDBottomNavTabClick(`Region_${shortName}`)}
                deviceType={deviceType}
              />
            </dd>
          </dl>
          <div
            className="flex-1 grid gap-[50px]"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' }}
          >
            {navList.map(
              (item, index) =>
                Boolean(
                  (item.content as { list: any[]; id: number; layout_title: string }).list,
                ) && (
                  <div key={item.id}>
                    <p className="text-primary-white text-14 font-bb block mb-[16px]">
                      <span>{(item.content as { layout_title: string }).layout_title}</span>
                    </p>
                    <div>
                      {(item.content as { list: any[]; id: number; layout_title: string }).list.map(
                        (sItem, id) => (
                          <Link
                            prefetch={false}
                            href={sItem.href}
                            className="text-14 mb-16 text-[#d4d4d4] block"
                            key={`${sItem.title}-${id}`}
                            onClick={(event) => {
                              event.preventDefault();
                              SDBottomNavTabClick(
                                (item.content as { layout_title: string }).layout_title,
                                sItem.title,
                                index,
                                id,
                              );
                              if (sItem.href?.includes('goods-list')) {
                                setSession('zl_goodsListEnterPageScene', '全局导航');
                              }
                              if (sItem.href === env.NEXT_PUBLIC_COOKIE_TARGET) {
                                window.OneTrust.ToggleInfoDisplay();
                              } else if (
                                (sItem.href.includes('zeelool.com') ||
                                  sItem.href.includes('xmslol.cn')) &&
                                !sItem.href.includes(env.NEXT_PUBLIC_HELP_CENTER_PATH)
                              ) {
                                const href = new URL(sItem.href);
                                const url = href.href.replace(href.origin, '');
                                router.push(url);
                              } else {
                                window.location.href = sItem.href;
                              }
                            }}
                          >
                            {sItem.title}
                          </Link>
                        ),
                      )}
                    </div>
                  </div>
                ),
            )}
          </div>
          <div className="flex items-end justify-end flex-col w-full mt-[100px] mb-[30px]">
            <Image
              src="/home-page/logo.png"
              alt="logo"
              width={960}
              height={130}
              className="w-[46.9%] h-auto min-w-[675px] mb-[30px]"
            />
            <ul className="flex items-end justify-end flex-wrap">
              <li className="m-[0_0_10px_16px]">
                <Link
                  prefetch={false}
                  className="text-16 uppercase text-primary-white font-br"
                  href="/zeelool-promise"
                  aria-label={t('zeeloolPromise')}
                >
                  {t('zeeloolPromise')}
                </Link>
              </li>
              <li className="m-[0_0_10px_16px]">
                <Link
                  prefetch={false}
                  className="text-16 uppercase text-primary-white font-br"
                  href="/return-refund"
                  aria-label={t('shippingReturns')}
                >
                  {t('shippingReturns')}
                </Link>
              </li>
              {/*
              德国站增加Impressum声明
              该页面在德国属于合规的一部分，目前已有用户评论没有该页面，为避免引起更多用户或竞对的投诉，增设该页面
              https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/req/5f79fe775bfe756df060632bc2
              */}
              {IS_DE && (
                <li className="m-[0_0_10px_16px]">
                  <Link
                    prefetch={false}
                    className="text-16 uppercase text-primary-white font-br"
                    href="/impressum"
                    aria-label="Impressum"
                  >
                    IMPRESSUM
                  </Link>
                </li>
              )}
              <li className="m-[0_0_10px_16px]">
                <Link
                  prefetch={false}
                  className="text-16 uppercase text-primary-white font-br"
                  href={locale === 'de' ? '/terms-conditions' : '/privacy-security'}
                  aria-label={t('privacySecurity')}
                >
                  {t('privacySecurity')}
                </Link>
              </li>
              <li className="m-[0_0_10px_16px]">
                <Link
                  prefetch={false}
                  className="text-16 uppercase text-primary-white font-br"
                  href={locale === 'de' ? '/agb' : '/terms-conditions'}
                  aria-label={t('termsConditions')}
                >
                  {t('termsConditions')}
                </Link>
              </li>
              {env.NEXT_PUBLIC_SHOW_KLARNA === 'true' && (
                <li className="m-[0_0_10px_16px]">
                  <Link
                    prefetch={false}
                    className="text-16 uppercase text-primary-white font-br"
                    aria-label={t('klarna')}
                    href="/klarna"
                  >
                    {t('klarna')}
                  </Link>
                </li>
              )}
              <li className="m-[0_0_10px_16px]">
                <Link
                  prefetch={false}
                  className="text-16 uppercase text-primary-white font-br"
                  href={env.NEXT_PUBLIC_COOKIE_TARGET}
                  aria-label={t('doNotSellMyPersonalInformation')}
                  onClick={(event) => {
                    if (env.NEXT_PUBLIC_ENABLE_ONETRUST === 'true') {
                      event.preventDefault();
                      window?.OneTrust?.ToggleInfoDisplay();
                    }
                  }}
                >
                  {t('doNotSellMyPersonalInformation')}
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="flex justify-between items-center w-full p-[0_120px_30px_190px] box-border bg-black">
          <div className="box-border">
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[position:-20px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-90px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-160px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-230px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-300px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-370px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-440px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            <span
              className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-510px_-20px]"
              style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
            />
            {env.NEXT_PUBLIC_SHOW_KLARNA === 'true' && (
              <span
                className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-580px_-20px]"
                style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
              />
            )}
            {env.NEXT_PUBLIC_SHOW_AFTER_PAY === 'true' && (
              <span
                className="inline-block w-[50px] h-[30px] mr-2 bg-no-repeat bg-[length:720px_70px] bg-[position:-650px_-20px]"
                style={{ backgroundImage: `url(${paymentMethodsPicture})` }}
              />
            )}
          </div>
          <p className="text-14 text-[#d4d4d4]">
            &copy; {new Date().getFullYear()} {t('rightsReserved')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Footer;
